import RemoteOrder from "../services/remote-order";

// Acompanha os eventos do pedido na API (GET /Apps/pedido/<id>/status) para a
// tela de status, em polling.
//
// Logo depois do pedido, a primeira consulta espera (`inicioMs`, ver
// atrasoInicial), porque o status ainda não mudou. Fora isso ela sai na
// partida. O atraso valia a cada abertura da tela, e quem voltava a ela,
// recarregava ou tinha a aba descartada via o status velho esse tempo todo. A
// próxima só sai depois que a anterior termina, e o timeout do RemoteOrder
// garante que ela termina.
//
// Na volta à aba (WhatsApp, app do banco no Pix, tela bloqueada) a consulta
// sai na hora, inclusive durante a espera inicial: em segundo plano o
// navegador congela ou estrangula os timers.
//
// Com `pausarOculta` (celular do cliente), o polling para enquanto a aba está
// oculta: ninguém está olhando, e a volta consulta na hora. Só pausa depois de
// um `visibilitychange` para oculta. O `visibilityState` lido a qualquer hora
// não basta, porque há WebView que diz `hidden` sem estar. POS e totem não
// pausam: o WebView deles pode reportar `hidden` errado, e eles vivem com a
// tela aberta.

/**
 * Quanto a primeira consulta espera: até `atrasoMs` depois da criação do
 * pedido, e nada se ele já é mais velho que isso (tela reaberta ou
 * recarregada). O `createdAt` vem do servidor; o teto em `atrasoMs` segura o
 * relógio atrasado do aparelho, que esticaria a espera.
 */
export function atrasoInicial(createdAt, atrasoMs, agora = Date.now()) {
  const criadoEm = new Date(createdAt).getTime();
  if (Number.isNaN(criadoEm)) return 0;
  return Math.min(atrasoMs, Math.max(0, criadoEm + atrasoMs - agora));
}

/**
 * @param {number} orderId o `id` da resposta do POST /pedido
 * @param {{ delayMs: number, inicioMs?: number, pausarOculta?: boolean,
 *   isFinished: (status: object[]) => boolean,
 *   onStatus: (status: object[]) => void }} opcoes
 * @returns {() => void} para tudo
 */
export default function watchOrderStatus(
  orderId,
  { delayMs, inicioMs = 0, pausarOculta = false, isFinished, onStatus },
) {
  let parado = false;
  let timer = null;
  let oculta = false;
  // Cada consulta tem a sua vez. A resposta de uma vez já passada (a volta à
  // aba começou outra) é descartada e não agenda nada: não nascem duas cadeias.
  let vez = 0;

  const consultar = async () => {
    clearTimeout(timer);
    // pausado: a volta à aba chama de novo e o ciclo recomeça dali
    if (pausarOculta && oculta) return;
    const minhaVez = ++vez;
    let terminou = false;
    try {
      const status = await RemoteOrder.retrieveStatus(orderId);
      if (parado || minhaVez !== vez) return;
      onStatus(status);
      terminou = isFinished(status);
    } catch (err) {
      if (parado || minhaVez !== vez) return;
      console.error(err);
    }

    if (terminou) {
      pararDeOuvir();
      return;
    }
    timer = setTimeout(consultar, delayMs);
  };

  const acordar = () => {
    if (!parado) consultar();
  };
  const aoMudarVisibilidade = () => {
    oculta = document.visibilityState === "hidden";
    if (document.visibilityState === "visible") acordar();
  };
  // só a volta pelo bfcache; o pageshow da carga normal já passou
  const aoMostrarPagina = (event) => {
    if (event.persisted) acordar();
  };

  function pararDeOuvir() {
    document.removeEventListener("visibilitychange", aoMudarVisibilidade);
    window.removeEventListener("pageshow", aoMostrarPagina);
    window.removeEventListener("online", acordar);
  }

  document.addEventListener("visibilitychange", aoMudarVisibilidade);
  window.addEventListener("pageshow", aoMostrarPagina);
  window.addEventListener("online", acordar);

  if (inicioMs > 0) {
    timer = setTimeout(consultar, inicioMs);
  } else {
    consultar();
  }

  return () => {
    parado = true;
    clearTimeout(timer);
    pararDeOuvir();
  };
}
